/**
 * BulkActionsBar Types
 *
 * Generic type definitions for the shared BulkActionsBar component.
 * Supports any entity type with configurable actions.
 */

import { ReactNode } from 'react';

// ============================================
// Action Types
// ============================================

export type BulkActionVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger';

export interface BulkActionConfirmation {
  /** Confirmation dialog title */
  title: string;
  /** Confirmation dialog message */
  message: string;
  /** Confirm button text (default: "Confirm") */
  confirmText?: string;
  /** Cancel button text (default: "Cancel") */
  cancelText?: string;
}

export interface BulkAction<T = unknown> {
  /** Unique identifier for the action */
  id: string;
  /** Display label for the action button */
  label: string;
  /** Icon component to render */
  icon: ReactNode;
  /** Visual variant */
  variant?: BulkActionVariant;
  /** Click handler - receives array of selected IDs */
  onClick: (selectedIds: string[]) => Promise<void>;
  /** Optional confirmation dialog before executing */
  confirm?: BulkActionConfirmation;
  /** Condition to show this action (default: always show) */
  show?: (selectedItems: T[]) => boolean;
  /** Condition to disable this action (default: never disable) */
  disabled?: (selectedItems: T[]) => boolean;
  /** Tooltip text when disabled */
  disabledTooltip?: string;
}

// ============================================
// Component Props
// ============================================

export interface BulkActionsBarProps<T = unknown> {
  /** Set of selected item IDs */
  selectedIds: Set<string>;
  /** Full list of items (for filtering selected items) */
  items: T[];
  /** Function to get item ID */
  getItemId: (item: T) => string;
  /** Bulk actions to display */
  actions: BulkAction<T>[];
  /** Called when selection should be cleared */
  onClearSelection: () => void;
  /** Entity name for display (e.g., "user", "partner", "organization") */
  entityName?: string;
  /** Custom class name for the container */
  className?: string;
  /** Color theme (default: purple) */
  theme?: 'purple' | 'blue' | 'green' | 'gray';
}

// ============================================
// Dropdown Menu Types (for role/status changes)
// ============================================

export interface DropdownOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

export interface BulkActionDropdown<T = unknown> extends Omit<BulkAction<T>, 'onClick'> {
  /** Dropdown options */
  options: DropdownOption[];
  /** Click handler with selected option value */
  onClick: (selectedIds: string[], optionValue: string) => Promise<void>;
}

// ============================================
// Utility Types
// ============================================

export type BulkActionItem<T> = BulkAction<T> | BulkActionDropdown<T>;

export function isDropdownAction<T>(action: BulkActionItem<T>): action is BulkActionDropdown<T> {
  return 'options' in action && Array.isArray((action as BulkActionDropdown<T>).options);
}
